import { useState, useRef, useCallback, useEffect } from "react";
import { api, getSessionId } from "wasp/client/api";
import { SCAN_STEPS, MIN_SCAN_MS } from "./scanSteps";

/**
 * Drives one Edgy analysis: explicit state machine + SSE consumption.
 *
 * The endpoint is POST (it carries a body), so EventSource is not usable — it only
 * does GET. We read the response body as a stream and parse `data:` frames by hand.
 */

export type EdgyState =
  | "IDLE"
  | "CHECKING"
  /** Plan is not Max while Edgy is in its Max-only beta. */
  | "BETA_LOCKED"
  | "MISSING_PREREQS"
  | "SELECT_STRATEGY"
  | "SCANNING"
  | "REVEALING"
  | "NO_CREDITS"
  | "ERROR";

export { SCAN_STEPS, MIN_SCAN_MS, type ScanStepId } from "./scanSteps";

export type ScanContext = { tradesCount: number; lowSample: boolean };

export type EdgyErrorInfo = {
  code: string;
  message: string;
  meta?: Record<string, unknown>;
};

type ServerEvent =
  | { type: "context_ready"; tradesCount: number; lowSample: boolean }
  | { type: "ai_thinking" }
  | { type: "block"; name: string }
  | { type: "done"; reportId: string }
  | { type: "error"; code: string; message: string; meta?: Record<string, unknown> };

export function useEdgyAnalysis() {
  const [state, setState] = useState<EdgyState>("IDLE");
  const [currentStep, setCurrentStep] = useState(0);
  const [scanContext, setScanContext] = useState<ScanContext | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [error, setError] = useState<EdgyErrorInfo | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const startedAtRef = useRef<number>(0);
  const stepTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    stepTimersRef.current.forEach(clearTimeout);
    stepTimersRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearTimers();
      abortRef.current?.abort();
    };
  }, [clearTimers]);

  const reset = useCallback(() => {
    clearTimers();
    abortRef.current?.abort();
    abortRef.current = null;
    setState("IDLE");
    setCurrentStep(0);
    setScanContext(null);
    setReportId(null);
    setError(null);
  }, [clearTimers]);

  /**
   * Paces the visible steps across MIN_SCAN_MS. Real SSE events can move the sequence
   * forward faster, but never past what has actually happened — the last step keeps
   * looping if the API is slow.
   */
  const scheduleSteps = useCallback(() => {
    clearTimers();
    const perStep = MIN_SCAN_MS / SCAN_STEPS.length;
    for (let i = 1; i < SCAN_STEPS.length; i++) {
      stepTimersRef.current.push(
        setTimeout(() => setCurrentStep((prev) => Math.max(prev, i)), perStep * i)
      );
    }
  }, [clearTimers]);

  const start = useCallback(
    async (params: { playbookId: string; language: "fr" | "en" }) => {
      clearTimers();
      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;
      startedAtRef.current = Date.now();

      setState("SCANNING");
      setCurrentStep(0);
      setScanContext(null);
      setError(null);
      setReportId(null);
      scheduleSteps();

      let pendingReportId: string | null = null;
      let failure: EdgyErrorInfo | null = null;

      try {
        // Cannot call through Wasp's `api` (axios) here: it buffers the whole
        // response and cannot stream. So the two things it normally handles are done
        // by hand — its baseURL (in dev the server runs on a different port than the
        // client, so a relative path would hit Vite) and the Authorization header.
        const sessionId = getSessionId();
        const baseUrl = (api.defaults.baseURL ?? "").replace(/\/$/, "");
        const response = await fetch(`${baseUrl}/api/edgy/analyze`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(sessionId ? { Authorization: `Bearer ${sessionId}` } : {}),
          },
          body: JSON.stringify({ playbookId: params.playbookId, language: params.language }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          // A non-2xx here means the stream never opened (auth, bad request).
          failure = { code: "AI_FAILED", message: `HTTP ${response.status}` };
        } else {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // SSE frames are separated by a blank line.
            const frames = buffer.split("\n\n");
            buffer = frames.pop() ?? "";

            for (const frame of frames) {
              const line = frame.split("\n").find((l) => l.startsWith("data:"));
              if (!line) continue;

              let event: ServerEvent;
              try {
                event = JSON.parse(line.slice(5).trim());
              } catch {
                continue;
              }

              switch (event.type) {
                case "context_ready":
                  setScanContext({ tradesCount: event.tradesCount, lowSample: event.lowSample });
                  setCurrentStep((prev) => Math.max(prev, 1));
                  break;
                case "ai_thinking":
                  setCurrentStep((prev) => Math.max(prev, 2));
                  break;
                case "block": {
                  // Map the streamed report sections onto the visible steps.
                  const stepByBlock: Record<string, number> = {
                    strengths: 3,
                    leaks: 3,
                    playbookGaps: 4,
                    weeklyActions: 4,
                    suggestedQuestions: 4,
                  };
                  const next = stepByBlock[event.name];
                  if (next !== undefined) setCurrentStep((prev) => Math.max(prev, next));
                  break;
                }
                case "done":
                  pendingReportId = event.reportId;
                  break;
                case "error":
                  failure = { code: event.code, message: event.message, meta: event.meta };
                  break;
              }
            }
          }
        }
      } catch (err: any) {
        if (err?.name === "AbortError") return; // user closed the drawer
        failure = { code: "AI_FAILED", message: err?.message ?? "Network error" };
      }

      clearTimers();

      // Hold the scan sequence for its minimum duration before revealing.
      const elapsed = Date.now() - startedAtRef.current;
      const remaining = Math.max(0, MIN_SCAN_MS - elapsed);
      if (remaining > 0 && !controller.signal.aborted) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      if (controller.signal.aborted) return;

      if (failure) {
        setError(failure);
        // BETA_MAX_ONLY is an entitlement refusal, not a failure: show the upgrade
        // screen rather than the generic "that didn't work" error.
        setState(
          failure.code === "NO_CREDITS"
            ? "NO_CREDITS"
            : failure.code === "BETA_MAX_ONLY"
              ? "BETA_LOCKED"
              : "ERROR"
        );
        return;
      }

      if (pendingReportId) {
        setCurrentStep(SCAN_STEPS.length);
        setReportId(pendingReportId);
        setState("REVEALING");
      } else {
        setError({ code: "AI_FAILED", message: "No report returned." });
        setState("ERROR");
      }
    },
    [clearTimers, scheduleSteps]
  );

  const cancel = useCallback(() => {
    clearTimers();
    abortRef.current?.abort();
    abortRef.current = null;
  }, [clearTimers]);

  return {
    state,
    setState,
    currentStep,
    scanContext,
    reportId,
    error,
    start,
    reset,
    cancel,
    isRunning: state === "SCANNING",
  };
}
